import { call, put, takeLatest } from "redux-saga/effects";
import { ActionWithPayload } from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";

import {
  ChatListResponse,
  ChatMessage,
  ChatsFilter,
  ChatStarter,
  IChat,
  IReactToMessageDto,
  IUpdateChatDto,
  TokenQuota,
} from "../interfaces";
import { deleteChat, getChat, getChatList, getQuota, getStarters, reactToMessage, updateChat } from "./actions";
import api from "../api";

export function* getChatListSaga({ payload }: ActionWithPayload<ChatsFilter>) {
  try {
    yield put(startLoading());
    const response: ChatListResponse = yield call(api.getChatList, payload);
    yield put(stopLoading());
    yield put(
      getChatList.success({
        ...response,
        is_clear: !payload.page,
      }),
    );
  } catch (error) {
    yield put(getChatList.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* getChatSaga({ payload }: ActionWithPayload<string>) {
  try {
    yield put(startLoading());
    const response: IChat = yield call(api.getChat, payload);
    yield put(stopLoading());
    yield put(getChat.success(response));
  } catch (error) {
    yield put(getChat.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* updateChatSaga({ payload }: ActionWithPayload<IUpdateChatDto>) {
  try {
    yield put(startLoading());
    const response: IChat = yield call(api.updateChat, payload);
    yield put(stopLoading());
    yield put(updateChat.success(response));
  } catch (error) {
    yield put(updateChat.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* deleteChatSaga({ payload }: ActionWithPayload<string>) {
  try {
    yield put(startLoading());
    // DELETE /chat/:uuid returns 204 No Content — no body to read the uuid back from, so the
    // success payload is built from the request's own payload (the uuid we already know).
    yield call(api.deleteChat, payload);
    yield put(stopLoading());
    yield put(deleteChat.success({ uuid: payload }));
  } catch (error) {
    yield put(deleteChat.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* reactToMessageSaga({ payload }: ActionWithPayload<IReactToMessageDto>) {
  try {
    yield put(startLoading());
    const response: ChatMessage = yield call(api.reactToMessage, payload);
    yield put(stopLoading());
    yield put(reactToMessage.success(response));
  } catch (error) {
    yield put(reactToMessage.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* getStartersSaga() {
  try {
    yield put(startLoading());
    const response: { chatStarters: ChatStarter[] } = yield call(api.getStarters);
    yield put(stopLoading());
    yield put(getStarters.success(response.chatStarters));
  } catch (error) {
    yield put(getStarters.failure(error as Error));
    yield put(stopLoading());
  }
}

export function* getQuotaSaga() {
  try {
    yield put(startLoading());
    const response: { quota: TokenQuota | null } = yield call(api.getQuota);
    yield put(stopLoading());
    yield put(getQuota.success(response));
  } catch (error) {
    yield put(getQuota.failure(error as Error));
    yield put(stopLoading());
  }
}

function* chatSagas() {
  yield takeLatest(getChatList.request, getChatListSaga);
  yield takeLatest(getChat.request, getChatSaga);
  yield takeLatest(updateChat.request, updateChatSaga);
  yield takeLatest(deleteChat.request, deleteChatSaga);
  yield takeLatest(reactToMessage.request, reactToMessageSaga);
  yield takeLatest(getStarters.request, getStartersSaga);
  yield takeLatest(getQuota.request, getQuotaSaga);
}

export default chatSagas;
